import { useState } from 'react';
import { C } from '../../lib/theme';
import { styles } from '../../lib/styles';
import { Dialog } from '../Dialog';
import { newIdempotencyKey } from '../../lib/idempotency';
import { useAppData } from '../../state/contexts/AppDataContext';
import { useUIState } from '../../state/contexts/UIStateContext';
import { ApiProblem } from '../../api/client';
import { createUser, type BcidRole } from '../../api/users';

// #139 — create a portal user. Federated pre-create (SSO users, no password) or
// local break-glass (server-generated temp password shown ONCE). On success the
// modal sets modalData.usersCreated so UserManagement re-loads the list.

const ROLE_LABELS: Record<BcidRole, string> = {
  'bcid-admin': 'Admin',
  'bcid-analyst': 'Analyst',
  'bcid-approver': 'Approver',
  'bcid-legal': 'Legal',
};
const ROLE_OPTIONS = Object.keys(ROLE_LABELS) as BcidRole[];

export function CreateUserModal() {
  const { showToast } = useAppData();
  const { setModal, setModalData } = useUIState();

  const [mode, setMode] = useState<'federated' | 'local'>('federated');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<BcidRole>('bcid-analyst');
  const [submitting, setSubmitting] = useState(false);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Marks the list stale so UserManagement reloads on close.
  const markCreated = () => setModalData((d: Record<string, unknown>) => ({ ...d, usersCreated: Date.now() }));

  const close = () => setModal(null);

  const submit = async () => {
    if (!email.trim()) { showToast('Email is required.'); return; }
    setSubmitting(true);
    try {
      const result = await createUser(
        { mode, email: email.trim(), firstName: firstName.trim() || undefined, lastName: lastName.trim() || undefined, role },
        newIdempotencyKey(),
      );
      markCreated();
      if (mode === 'local' && result.tempPassword) {
        // Stay open to show the one-time password.
        setTempPassword(result.tempPassword);
      } else {
        showToast('User created. They can sign in via their identity provider.', 'success');
        close();
      }
    } catch (err) {
      if (err instanceof ApiProblem) showToast(err.detail ? `${err.title}: ${err.detail}` : err.title);
      else showToast(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const copy = () => {
    if (!tempPassword) return;
    navigator.clipboard?.writeText(tempPassword).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => showToast('Copy failed — select and copy the password manually.'));
  };

  // Success view: the temp password is shown once and never again.
  if (tempPassword) {
    return (
      <Dialog title="Local user created" onClose={close}>
        <div style={{ fontSize: 13, color: C.text, marginBottom: 12 }}>
          The account <strong>{email}</strong> was created with a temporary password. It is shown
          <strong> once</strong> — copy it now and hand it to the user out of band (not by email).
          They must change it at first sign-in.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <code style={{ flex: 1, padding: '10px 12px', background: C.bgInput, border: `2px solid ${C.border}`, borderRadius: 8, fontSize: 14, wordBreak: 'break-all' }} data-testid="temp-password">{tempPassword}</code>
          <button style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }} onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
        </div>
        <div style={{ fontSize: 11, color: C.warn, marginBottom: 16 }}>⚠ This password is not stored anywhere and cannot be retrieved again. If you lose it, send a password-reset instead.</div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button style={{ ...styles.btn, ...styles.btnGold }} onClick={close}>Done</button>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog title="Create Portal User" onClose={close}>
      <fieldset style={{ border: 'none', padding: 0, margin: '0 0 16px' }}>
        <legend style={{ fontSize: 11, color: C.textMuted, marginBottom: 6 }}>Account type</legend>
        <label style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>
          <input type="radio" name="cu-mode" checked={mode === 'federated'} onChange={() => setMode('federated')} /> Federated (SSO) — signs in through Entra ID or Google
        </label>
        <label style={{ display: 'block', fontSize: 13 }}>
          <input type="radio" name="cu-mode" checked={mode === 'local'} onChange={() => setMode('local')} /> Local (break-glass) — a portal password account with MFA
        </label>
      </fieldset>

      <div style={{ marginBottom: 12 }}>
        <label htmlFor="cu-email" style={{ fontSize: 11, color: C.textMuted }}>Email</label>
        <input id="cu-email" type="email" style={styles.input} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@example.com" />
      </div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <label htmlFor="cu-first" style={{ fontSize: 11, color: C.textMuted }}>First name</label>
          <input id="cu-first" style={styles.input} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
        </div>
        <div style={{ flex: 1 }}>
          <label htmlFor="cu-last" style={{ fontSize: 11, color: C.textMuted }}>Last name</label>
          <input id="cu-last" style={styles.input} value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <label htmlFor="cu-role" style={{ fontSize: 11, color: C.textMuted }}>Role</label>
        <select id="cu-role" style={{ ...styles.select, width: '100%' }} value={role} onChange={(e) => setRole(e.target.value as BcidRole)}>
          {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
        </select>
      </div>
      {mode === 'federated' && role === 'bcid-admin' && (
        <div style={{ fontSize: 11, color: C.warn, marginBottom: 16 }}>
          ⚠ Admin for Entra-linked users is managed by the SGU-BCID-Admins group and is re-applied on
          each sign-in. Add the user to that group in Entra ID for a durable admin grant.
        </div>
      )}
      <div style={{ display: 'flex', gap: 12 }}>
        <button style={{ ...styles.btn, ...styles.btnGold }} disabled={submitting} onClick={submit}>{submitting ? 'Creating…' : 'Create User'}</button>
        <button style={{ ...styles.btn, ...styles.btnOutline }} disabled={submitting} onClick={close}>Cancel</button>
      </div>
    </Dialog>
  );
}
